import { useMutation } from '@tanstack/react-query';
import {
  Banner,
  Button,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Strong,
  Text,
  useModal,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { exportObjectData } from '@/services/db/object/mutations';
import { generatePath } from '@/services/router/generate-path';
import { useMountEffect } from '@/hooks/state/use-mount-effect';

export type ExportObjectModalData = {
  workspaceId: number;
  displayName: string;
  cioObjectId: string;
};

/** The confirm step: warn about sensitive data, then hand off to the progress modal. */
export function ExportObjectModal({
  data,
  modalState,
  onClose,
  onConfirm,
}: PlumaManagedModalProps<ExportObjectModalData>) {
  return (
    <Modal state={modalState} data-test-export-object-modal title="Export profile data">
      <ModalBody data-test-export-object-modal-body>
        <Paragraph mb="200">
          You're about to export all <Strong className="fs-mask">{data.displayName}</Strong> data. This includes all of
          its attributes and relationships.
        </Paragraph>
        <Banner variant="caution" mb="300" title="Warning">
          If there is sensitive data (authentication keys, for example) contained in any of the above, it{' '}
          <Strong>will be in your export</Strong>. Review it carefully before sharing outside your company.
        </Banner>
        <Paragraph>Are you sure?</Paragraph>
      </ModalBody>
      <ModalFooter>
        <Button data-test-export-object-cancel-button variant="secondary" onClick={() => onClose()}>
          Cancel
        </Button>
        <Button data-test-export-object-confirm-button variant="primary" onClick={() => onConfirm()}>
          Yes, export data
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export function useExportObjectModal() {
  return useModal(ExportObjectModal);
}

/**
 * The progress step: fires the export on open and reports its outcome. The
 * export itself is fulfilled asynchronously by email, so success means
 * "accepted", not "downloadable".
 */
export function ExportObjectProgressModal({
  data,
  modalState,
  onClose,
}: PlumaManagedModalProps<ExportObjectModalData>) {
  const exportsHref = generatePath('/workspaces/:workspaceId/journeys/exports', { workspaceId: data.workspaceId });
  const exportMutation = useMutation({
    mutationFn: () =>
      exportObjectData(data.workspaceId, { internal_id: data.cioObjectId, exported_from: 'object_profile' }),
  });
  const startExport = exportMutation.mutate;

  useMountEffect(() => {
    startExport();
  });

  return (
    <Modal state={modalState} title="Export profile data">
      <ModalBody>
        <Paragraph data-test-cio-export-summary>
          {exportMutation.isError ? (
            <Text data-test-export-summary-failure>There was a problem processing your export. Try again.</Text>
          ) : exportMutation.isSuccess ? (
            <Text data-test-export-summary-success>
              Your export is currently processing. When it is ready, you will receive an email, and it will be available
              for download on the{' '}
              <Link isUnderlined href={exportsHref}>
                exports page
              </Link>
              .
            </Text>
          ) : (
            <Text data-test-export-summary-pending>We are starting your export now…</Text>
          )}
        </Paragraph>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={() => onClose()} isDisabled={exportMutation.isPending}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export function useExportObjectProgressModal() {
  return useModal(ExportObjectProgressModal);
}
